import { FunctionComponent, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useCreateFilterTagMutation,
    useGetFilterTagsQuery,
} from '@/api/v2/filterTags';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { FilterTag } from '@/models/product';

type FilterTagChipSelectProps = {
    value: string[];
    /**
     * A tag created inline is handed over as well: the query cache still holds the previous list at this point,
     * so the parent cannot resolve the new id on its own and would drop the assignment.
     */
    onChange: (filterTagIds: string[], createdFilterTag?: FilterTag) => void;
    label?: string;
};

const SortableChip = ({
    id,
    label,
    onDelete,
}: {
    id: string;
    label: string;
    onDelete: () => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Chip
                label={label}
                onDelete={onDelete}
                color='primary'
                sx={{ cursor: 'grab' }}
            />
        </Box>
    );
};

export const FilterTagChipSelect: FunctionComponent<
    FilterTagChipSelectProps
> = ({ value, onChange, label = 'Filtertags' }) => {
    // liftOperatorData is null until the stored session is rehydrated, see FilterTagsPage.
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;
    const { addNotification } = useNotification();
    const { data: filterTags = [] } = useGetFilterTagsQuery(
        { liftOperatorId },
        { skip: !liftOperatorId }
    );
    const [createFilterTag, { isLoading: isCreating }] =
        useCreateFilterTagMutation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newNameDe, setNewNameDe] = useState('');
    const [newNameEn, setNewNameEn] = useState('');

    const selected = useMemo(
        () =>
            (value ?? [])
                .map((id) => filterTags.find((t) => t.id === id))
                .filter((t): t is FilterTag => t != null),
        [filterTags, value]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = value.indexOf(String(active.id));
        const newIndex = value.indexOf(String(over.id));
        onChange(arrayMove(value, oldIndex, newIndex));
    };

    const handleCreate = async () => {
        if (!newNameDe.trim()) return;
        try {
            const created = await createFilterTag({
                liftOperatorId,
                payload: {
                    nameDe: newNameDe.trim(),
                    nameEn: newNameEn.trim() || null,
                },
            }).unwrap();
            onChange([...value, created.id], created);
            setNewNameDe('');
            setNewNameEn('');
            setCreateDialogOpen(false);
        } catch {
            addNotification({
                type: 'error',
                message: 'Filtertag konnte nicht angelegt werden',
            });
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
                <Autocomplete
                    sx={{ flex: 1 }}
                    multiple
                    options={filterTags}
                    getOptionLabel={(option: FilterTag) =>
                        option.nameEn
                            ? `${option.nameDe} / ${option.nameEn}`
                            : option.nameDe
                    }
                    value={selected}
                    onChange={(_, newValue) =>
                        onChange(newValue.map((v) => v.id))
                    }
                    renderInput={(params) => (
                        <TextField {...params} label={label} />
                    )}
                    renderTags={() => null}
                />
                <Button
                    variant='outlined'
                    onClick={() => setCreateDialogOpen(true)}
                >
                    + Neu
                </Button>
            </Stack>

            {selected.length > 0 && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={value}
                        strategy={horizontalListSortingStrategy}
                    >
                        <Typography variant='caption' sx={{ mt: 2, mb: 1 }}>
                            Zugewiesene Filtertags (Reihenfolge per Drag and
                            Drop):
                        </Typography>
                        <Stack
                            direction='row'
                            spacing={1}
                            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
                        >
                            {selected.map((t) => (
                                <SortableChip
                                    key={t.id}
                                    id={t.id}
                                    label={
                                        t.nameEn
                                            ? `${t.nameDe} / ${t.nameEn}`
                                            : t.nameDe
                                    }
                                    onDelete={() =>
                                        onChange(
                                            value.filter((id) => id !== t.id)
                                        )
                                    }
                                />
                            ))}
                        </Stack>
                    </SortableContext>
                </DndContext>
            )}

            <Dialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle>Neuen Filtertag anlegen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label='Bezeichnung Deutsch'
                            value={newNameDe}
                            onChange={(e) => setNewNameDe(e.target.value)}
                            autoFocus
                            required
                        />
                        <TextField
                            label='Bezeichnung Englisch (optional)'
                            value={newNameEn}
                            onChange={(e) => setNewNameEn(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={!newNameDe.trim() || isCreating}
                        variant='contained'
                    >
                        Anlegen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
